import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, Copy, Check, Wallet, TrendingUp, Clock, DollarSign } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function ProjectDetails() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [orders, setOrders] = useState([]);
  const [wallets, setWallets] = useState([]);
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [showWalletModal, setShowWalletModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [copiedCode, setCopiedCode] = useState(null);

  const [orderForm, setOrderForm] = useState({
    amount: '',
    currency: 'VES',
    description: '',
    order_reference: '',
    payer_email: '',
    payer_name: '',
    payer_document: '',
    payer_phone: '',
    expires_in_minutes: 30,
    payee_wallets: []
  });

  const [walletForm, setWalletForm] = useState({
    wallet_name: '',
    wallet_type: 'banco_plaza',
    currency: 'VES'
  });

  useEffect(() => {
    loadProjectDetails();
  }, [projectId]);

  const loadProjectDetails = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const [projectRes, ordersRes, walletsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/projects/${projectId}`, { headers }),
        axios.get(`${BACKEND_URL}/api/projects/${projectId}/orders`, { headers }),
        axios.get(`${BACKEND_URL}/api/projects/${projectId}/wallets`, { headers })
      ]);

      setProject(projectRes.data);
      setOrders(ordersRes.data);
      setWallets(walletsRes.data);
    } catch (error) {
      console.error('Error loading project:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateOrder = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const orderData = {
        ...orderForm,
        project_id: projectId,
        amount: parseFloat(orderForm.amount),
        expires_in_minutes: parseInt(orderForm.expires_in_minutes)
      };

      await axios.post(`${BACKEND_URL}/api/orders`, orderData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setShowOrderModal(false);
      setOrderForm({
        amount: '',
        currency: 'VES',
        description: '',
        order_reference: '',
        payer_email: '',
        payer_name: '',
        payer_document: '',
        payer_phone: '',
        expires_in_minutes: 30,
        payee_wallets: []
      });
      loadProjectDetails();
    } catch (error) {
      console.error('Error creating order:', error);
      alert(error.response?.data?.detail || 'Error al crear orden');
    }
  };

  const handleCreateWallet = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const walletData = {
        ...walletForm,
        project_id: projectId
      };

      await axios.post(`${BACKEND_URL}/api/wallets`, walletData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setShowWalletModal(false);
      setWalletForm({
        wallet_name: '',
        wallet_type: 'banco_plaza',
        currency: 'VES'
      });
      loadProjectDetails();
    } catch (error) {
      console.error('Error creating wallet:', error);
      alert(error.response?.data?.detail || 'Error al crear wallet');
    }
  };

  const addPayeeWallet = () => {
    setOrderForm({
      ...orderForm,
      payee_wallets: [
        ...orderForm.payee_wallets,
        { wallet_id: '', percentage: 0, description: '' }
      ]
    });
  };

  const removePayeeWallet = (index) => {
    const newPayees = orderForm.payee_wallets.filter((_, i) => i !== index);
    setOrderForm({ ...orderForm, payee_wallets: newPayees });
  };

  const updatePayeeWallet = (index, field, value) => {
    const newPayees = [...orderForm.payee_wallets];
    newPayees[index][field] = value;
    setOrderForm({ ...orderForm, payee_wallets: newPayees });
  };

  const copyToClipboard = (text, code) => {
    navigator.clipboard.writeText(text);
    setCopiedCode(code);
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const getTotalDispersionPercentage = () => {
    return orderForm.payee_wallets.reduce((sum, p) => sum + (parseFloat(p.percentage) || 0), 0);
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">Cargando...</div>;
  }

  if (!project) {
    return <div className="min-h-screen flex items-center justify-center">Proyecto no encontrado</div>;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="border-b" style={{ backgroundColor: '#051C2C' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center gap-4">
              <button onClick={() => navigate('/projects')} className="text-white hover:text-gray-300">
                <ArrowLeft className="w-5 h-5" />
              </button>
              <img src="/logos/logo-fondo-oscuro.png" alt="Caura Pay" className="h-8" />
              <div className="border-l border-white/30 pl-4">
                <span className="text-xl font-bold text-white">{project.project_name}</span>
                <p className="text-sm text-gray-300">Comisión: {project.fee_percentage}%</p>
              </div>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setShowWalletModal(true)}
                className="px-4 py-2 border border-white/30 text-white rounded-lg hover:bg-white/10"
              >
                <Wallet className="w-4 h-4 inline mr-2" />
                Nueva Wallet
              </button>
              <button
                onClick={() => setShowOrderModal(true)}
                className="px-4 py-2 bg-white text-gray-800 rounded-lg hover:bg-gray-100"
              >
                <Plus className="w-4 h-4 inline mr-2" />
                Nueva Orden
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Wallets Section */}
        <div className="mb-8">
          <h2 className="text-xl font-bold mb-4" style={{ color: '#051C2C' }}>Wallets</h2>
          {wallets.length === 0 ? (
            <div className="bg-white rounded-xl p-8 text-center border border-gray-200">
              <Wallet className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p className="text-gray-600 mb-4">No hay wallets configuradas</p>
              <button
                onClick={() => setShowWalletModal(true)}
                className="px-6 py-2 text-white rounded-lg"
                style={{ backgroundColor: '#003594' }}
              >
                Crear Primera Wallet
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {wallets.map((wallet) => (
                <div key={wallet.wallet_id} className="bg-white rounded-xl p-6 border border-gray-200">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="font-semibold" style={{ color: '#051C2C' }}>{wallet.wallet_name}</h3>
                      <p className="text-xs text-gray-500">{wallet.wallet_type.replace('_', ' ').toUpperCase()}</p>
                    </div>
                    <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded-full">
                      {wallet.status}
                    </span>
                  </div>
                  <div className="space-y-2">
                    <div>
                      <p className="text-xs text-gray-600">Balance</p>
                      <p className="text-2xl font-bold" style={{ color: '#003594' }}>
                        {wallet.currency} {wallet.balance.toFixed(2)}
                      </p>
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <div>
                        <p className="text-gray-600">Recibido</p>
                        <p className="font-semibold text-green-600">+{wallet.total_received.toFixed(2)}</p>
                      </div>
                      <div>
                        <p className="text-gray-600">Enviado</p>
                        <p className="font-semibold text-red-600">-{wallet.total_sent.toFixed(2)}</p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Orders Section */}
        <div>
          <h2 className="text-xl font-bold mb-4" style={{ color: '#051C2C' }}>Órdenes de Pago</h2>
          {orders.length === 0 ? (
            <div className="bg-white rounded-xl p-8 text-center border border-gray-200">
              <TrendingUp className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p className="text-gray-600 mb-4">No hay órdenes creadas</p>
              <button
                onClick={() => setShowOrderModal(true)}
                className="px-6 py-2 text-white rounded-lg"
                style={{ backgroundColor: '#003594' }}
              >
                Crear Primera Orden
              </button>
            </div>
          ) : (
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Código</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Cliente</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Monto</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Estado</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Expira</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {orders.map((order) => (
                    <tr key={order.order_id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <code className="text-sm font-bold" style={{ color: '#003594' }}>
                          {order.order_code}
                        </code>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">{order.payer_name}</td>
                      <td className="px-6 py-4 text-sm font-semibold">
                        {order.currency} {order.amount.toFixed(2)}
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                          order.status === 'paid' ? 'bg-green-100 text-green-800' :
                          order.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                          order.status === 'expired' ? 'bg-gray-100 text-gray-800' :
                          'bg-red-100 text-red-800'
                        }`}>
                          {order.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {new Date(order.expires_at).toLocaleString('es-VE', { 
                          day: '2-digit', 
                          month: '2-digit', 
                          hour: '2-digit', 
                          minute: '2-digit' 
                        })}
                      </td>
                      <td className="px-6 py-4">
                        <button
                          onClick={() => copyToClipboard(order.payment_url, order.order_code)}
                          className="text-sm px-3 py-1 rounded-lg transition-colors"
                          style={{ backgroundColor: '#E8F3FA', color: '#003594' }}
                          title="Copiar link de pago"
                        >
                          {copiedCode === order.order_code ? (
                            <Check className="w-4 h-4 inline" />
                          ) : (
                            <Copy className="w-4 h-4 inline" />
                          )}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Create Order Modal */}
      {showOrderModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full my-8">
            <div className="p-6 border-b">
              <h2 className="text-xl font-bold" style={{ color: '#051C2C' }}>Nueva Orden de Pago</h2>
              <p className="text-sm text-gray-600 mt-1">Crear orden con dispersión automática</p>
            </div>

            <form onSubmit={handleCreateOrder} className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
              {/* Order Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Monto *</label>
                  <input
                    type="number"
                    step="0.01"
                    value={orderForm.amount}
                    onChange={(e) => setOrderForm({ ...orderForm, amount: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Moneda</label>
                  <select
                    value={orderForm.currency}
                    onChange={(e) => setOrderForm({ ...orderForm, currency: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg"
                  >
                    <option value="VES">VES (Bolívares)</option>
                    <option value="USD">USD (Dólares)</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Descripción *</label>
                <input
                  type="text"
                  value={orderForm.description}
                  onChange={(e) => setOrderForm({ ...orderForm, description: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Referencia Interna *</label>
                <input
                  type="text"
                  value={orderForm.order_reference}
                  onChange={(e) => setOrderForm({ ...orderForm, order_reference: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                  placeholder="ORDER-123"
                  required
                />
              </div>

              {/* Payer Info */}
              <div className="border-t pt-4">
                <h3 className="font-semibold mb-3" style={{ color: '#003594' }}>Información del Pagador</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Nombre *</label>
                    <input
                      type="text"
                      value={orderForm.payer_name}
                      onChange={(e) => setOrderForm({ ...orderForm, payer_name: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                    <input
                      type="email"
                      value={orderForm.payer_email}
                      onChange={(e) => setOrderForm({ ...orderForm, payer_email: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Documento *</label>
                    <input
                      type="text"
                      value={orderForm.payer_document}
                      onChange={(e) => setOrderForm({ ...orderForm, payer_document: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg"
                      placeholder="V12345678"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Teléfono *</label>
                    <input
                      type="text"
                      value={orderForm.payer_phone}
                      onChange={(e) => setOrderForm({ ...orderForm, payer_phone: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg"
                      placeholder="04241234567"
                      required
                    />
                  </div>
                </div>
              </div>

              {/* Dispersion Configuration */}
              <div className="border-t pt-4">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <h3 className="font-semibold" style={{ color: '#003594' }}>
                      Dispersión Automática
                    </h3>
                    <p className="text-xs text-gray-600">
                      Distribuir el pago automáticamente entre wallets (Total: {getTotalDispersionPercentage()}%)
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={addPayeeWallet}
                    className="text-sm px-3 py-1 rounded-lg"
                    style={{ backgroundColor: '#E8F3FA', color: '#003594' }}
                  >
                    <Plus className="w-4 h-4 inline mr-1" />
                    Agregar
                  </button>
                </div>

                {orderForm.payee_wallets.length > 0 && (
                  <div className="space-y-2">
                    {orderForm.payee_wallets.map((payee, index) => (
                      <div key={index} className="flex gap-2 items-start p-3 bg-gray-50 rounded-lg">
                        <div className="flex-1">
                          <select
                            value={payee.wallet_id}
                            onChange={(e) => updatePayeeWallet(index, 'wallet_id', e.target.value)}
                            className="w-full px-2 py-1 text-sm border rounded mb-2"
                          >
                            <option value="">Seleccionar wallet...</option>
                            {wallets.map((w) => (
                              <option key={w.wallet_id} value={w.wallet_id}>
                                {w.wallet_name} - {w.currency} {w.balance.toFixed(2)}
                              </option>
                            ))}
                          </select>
                          <div className="grid grid-cols-2 gap-2">
                            <input
                              type="number"
                              step="0.1"
                              placeholder="% Porcentaje"
                              value={payee.percentage}
                              onChange={(e) => updatePayeeWallet(index, 'percentage', e.target.value)}
                              className="w-full px-2 py-1 text-sm border rounded"
                            />
                            <input
                              type="text"
                              placeholder="Descripción"
                              value={payee.description}
                              onChange={(e) => updatePayeeWallet(index, 'description', e.target.value)}
                              className="w-full px-2 py-1 text-sm border rounded"
                            />
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => removePayeeWallet(index)}
                          className="text-red-600 hover:text-red-800 mt-1"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="flex gap-3 pt-4 border-t">
                <button
                  type="button"
                  onClick={() => setShowOrderModal(false)}
                  className="flex-1 px-4 py-2 border rounded-lg hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 text-white rounded-lg"
                  style={{ backgroundColor: '#003594' }}
                >
                  Crear Orden
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Create Wallet Modal */}
      {showWalletModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full">
            <div className="p-6 border-b">
              <h2 className="text-xl font-bold" style={{ color: '#051C2C' }}>Nueva Wallet</h2>
            </div>

            <form onSubmit={handleCreateWallet} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nombre *</label>
                <input
                  type="text"
                  value={walletForm.wallet_name}
                  onChange={(e) => setWalletForm({ ...walletForm, wallet_name: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                  placeholder="Wallet Principal"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Tipo</label>
                <select
                  value={walletForm.wallet_type}
                  onChange={(e) => setWalletForm({ ...walletForm, wallet_type: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                >
                  <option value="banco_plaza">Banco Plaza</option>
                  <option value="stripe">Stripe</option>
                  <option value="paypal">PayPal</option>
                  <option value="crypto">Crypto</option>
                  <option value="internal">Interna</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Moneda</label>
                <select
                  value={walletForm.currency}
                  onChange={(e) => setWalletForm({ ...walletForm, currency: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                >
                  <option value="VES">VES (Bolívares)</option>
                  <option value="USD">USD (Dólares)</option>
                </select>
              </div>

              <div className="flex gap-3 pt-4 border-t">
                <button
                  type="button"
                  onClick={() => setShowWalletModal(false)}
                  className="flex-1 px-4 py-2 border rounded-lg hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 text-white rounded-lg"
                  style={{ backgroundColor: '#003594' }}
                >
                  Crear Wallet
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
